import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { fetchPartner, updatePartner, deletePartner } from '../../api/partners';

const PARTNER_TIERS = [
  { value: 'campus_micro_factory', label: 'Campus Micro Factory' },
  { value: 'micro_factory', label: 'Micro Factory' },
  { value: 'regional_hub', label: 'Regional Hub' },
  { value: 'specialized_center', label: 'Specialized Center' },
];

const PARTNER_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'offboarded', label: 'Offboarded' },
];

const API_ACCESS_LEVELS = [
  { value: 'standard', label: 'Standard' },
  { value: 'restricted', label: 'Restricted' },
  { value: 'internal', label: 'Internal' },
];

const PartnerDetailPage = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();

  const [partner, setPartner] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadPartner = async () => {
    const data = await fetchPartner(partnerId);
    setPartner(data);
    setFormData({
      tier: data.tier || '',
      status: data.status || '',
      apiAccessLevel: data.apiAccessLevel || 'standard',
      productionCapacity: {
        minUnits: data.productionCapacity?.minUnits ?? '',
        maxUnits: data.productionCapacity?.maxUnits ?? '',
        monthlyCapacity: data.productionCapacity?.monthlyCapacity ?? '',
      },
      operationalProfile: {
        shippingRadius: data.operationalProfile?.shippingRadius ?? '',
        leadTimes: {
          standard: data.operationalProfile?.leadTimes?.standard ?? '',
          rush: data.operationalProfile?.leadTimes?.rush ?? '',
        },
        qualityMetrics: {
          defectRate: data.operationalProfile?.qualityMetrics?.defectRate ?? '',
          onTimeDelivery: data.operationalProfile?.qualityMetrics?.onTimeDelivery ?? '',
        },
        supportedMaterials: (data.operationalProfile?.supportedMaterials || []).join(', '),
        garmentTypes: (data.operationalProfile?.garmentTypes || []).join(', '),
      },
      notes: data.onboarding?.approvalNotes || '',
    });
  };

  useEffect(() => {
    let isMounted = true;

    const initialise = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPartner(partnerId);
        if (!isMounted) return;
        setPartner(data);
        setFormData({
          tier: data.tier || '',
          status: data.status || '',
          apiAccessLevel: data.apiAccessLevel || 'standard',
          productionCapacity: {
            minUnits: data.productionCapacity?.minUnits ?? '',
            maxUnits: data.productionCapacity?.maxUnits ?? '',
            monthlyCapacity: data.productionCapacity?.monthlyCapacity ?? '',
          },
          operationalProfile: {
            shippingRadius: data.operationalProfile?.shippingRadius ?? '',
            leadTimes: {
              standard: data.operationalProfile?.leadTimes?.standard ?? '',
              rush: data.operationalProfile?.leadTimes?.rush ?? '',
            },
            qualityMetrics: {
              defectRate: data.operationalProfile?.qualityMetrics?.defectRate ?? '',
              onTimeDelivery: data.operationalProfile?.qualityMetrics?.onTimeDelivery ?? '',
            },
            supportedMaterials: (data.operationalProfile?.supportedMaterials || []).join(', '),
            garmentTypes: (data.operationalProfile?.garmentTypes || []).join(', '),
          },
          notes: data.onboarding?.approvalNotes || '',
        });
      } catch (err) {
        console.error('Failed to load partner', err);
        if (isMounted) {
          setError('Unable to load partner details.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initialise();

    return () => {
      isMounted = false;
    };
  }, [partnerId]);

  const handleChange = (path, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      const keys = path.split('.');
      let cursor = updated;
      while (keys.length > 1) {
        const key = keys.shift();
        cursor[key] = cursor[key] !== undefined ? { ...cursor[key] } : {};
        cursor = cursor[key];
      }
      cursor[keys[0]] = value;
      return updated;
    });
  };

  const hasChanges = useMemo(() => {
    if (!partner || !formData) return false;
    const compareValue = (val) => (val ?? '').toString();
    const originalMaterials = (partner.operationalProfile?.supportedMaterials || []).join(', ');
    const originalGarments = (partner.operationalProfile?.garmentTypes || []).join(', ');

    return (
      partner.tier !== formData.tier ||
      partner.status !== formData.status ||
      partner.apiAccessLevel !== formData.apiAccessLevel ||
      compareValue(partner.productionCapacity?.minUnits) !== compareValue(formData.productionCapacity.minUnits) ||
      compareValue(partner.productionCapacity?.maxUnits) !== compareValue(formData.productionCapacity.maxUnits) ||
      compareValue(partner.productionCapacity?.monthlyCapacity) !==
        compareValue(formData.productionCapacity.monthlyCapacity) ||
      compareValue(partner.operationalProfile?.shippingRadius) !== compareValue(formData.operationalProfile.shippingRadius) ||
      compareValue(partner.operationalProfile?.leadTimes?.standard) !==
        compareValue(formData.operationalProfile.leadTimes.standard) ||
      compareValue(partner.operationalProfile?.leadTimes?.rush) !==
        compareValue(formData.operationalProfile.leadTimes.rush) ||
      compareValue(partner.operationalProfile?.qualityMetrics?.defectRate) !==
        compareValue(formData.operationalProfile.qualityMetrics.defectRate) ||
      compareValue(partner.operationalProfile?.qualityMetrics?.onTimeDelivery) !==
        compareValue(formData.operationalProfile.qualityMetrics.onTimeDelivery) ||
      originalMaterials !== formData.operationalProfile.supportedMaterials ||
      originalGarments !== formData.operationalProfile.garmentTypes ||
      (partner.onboarding?.approvalNotes || '') !== (formData.notes || '')
    );
  }, [partner, formData]);

  const handleSave = async () => {
    if (!formData) return;
    try {
      setSaving(true);

      const payload = {
        tier: formData.tier,
        status: formData.status,
        apiAccessLevel: formData.apiAccessLevel,
        productionCapacity: {
          minUnits: formData.productionCapacity.minUnits || undefined,
          maxUnits: formData.productionCapacity.maxUnits || undefined,
          monthlyCapacity: formData.productionCapacity.monthlyCapacity || undefined,
        },
        operationalProfile: {
          shippingRadius: formData.operationalProfile.shippingRadius || undefined,
          leadTimes: {
            standard: formData.operationalProfile.leadTimes.standard || undefined,
            rush: formData.operationalProfile.leadTimes.rush || undefined,
          },
          qualityMetrics: {
            defectRate: formData.operationalProfile.qualityMetrics.defectRate || undefined,
            onTimeDelivery: formData.operationalProfile.qualityMetrics.onTimeDelivery || undefined,
          },
          supportedMaterials: formData.operationalProfile.supportedMaterials
            ? formData.operationalProfile.supportedMaterials.split(',').map((item) => item.trim()).filter(Boolean)
            : [],
          garmentTypes: formData.operationalProfile.garmentTypes
            ? formData.operationalProfile.garmentTypes.split(',').map((item) => item.trim()).filter(Boolean)
            : [],
        },
        notes: formData.notes,
      };

      const updated = await updatePartner(partnerId, payload);
      toast.success('Partner updated');
      setPartner(updated);
      await loadPartner();
    } catch (err) {
      console.error('Failed to update partner', err);
      toast.error(err?.response?.data?.error || 'Failed to update partner details');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!partner) return;
    const confirmed = window.confirm(
      'Are you sure you want to delete or offboard this partner? This action cannot be easily undone.'
    );
    if (!confirmed) return;

    try {
      setSaving(true);
      await deletePartner(partnerId);
      toast.success('Partner removed from active network');
      navigate('/partners');
    } catch (err) {
      console.error('Failed to delete partner', err);
      toast.error(err?.response?.data?.error || 'Failed to delete partner');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !formData) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/partners')} sx={{ mb: 3 }}>
        Back to Partners
      </Button>

      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {partner?.company?.legalName || 'Partner Details'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update partner tier, status, and operational details.
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Tier"
                  value={formData.tier}
                  onChange={(e) => handleChange('tier', e.target.value)}
                >
                  {PARTNER_TIERS.map((tier) => (
                    <MenuItem key={tier.value} value={tier.value}>
                      {tier.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  {PARTNER_STATUSES.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="API Access Level"
                  value={formData.apiAccessLevel}
                  onChange={(e) => handleChange('apiAccessLevel', e.target.value)}
                >
                  {API_ACCESS_LEVELS.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Min Units"
                  type="number"
                  value={formData.productionCapacity.minUnits}
                  onChange={(e) => handleChange('productionCapacity.minUnits', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Max Units"
                  type="number"
                  value={formData.productionCapacity.maxUnits}
                  onChange={(e) => handleChange('productionCapacity.maxUnits', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Monthly Capacity"
                  type="number"
                  value={formData.productionCapacity.monthlyCapacity}
                  onChange={(e) => handleChange('productionCapacity.monthlyCapacity', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Shipping Radius"
                  value={formData.operationalProfile.shippingRadius}
                  onChange={(e) => handleChange('operationalProfile.shippingRadius', e.target.value)}
                  placeholder="e.g. 500 miles"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Standard Lead Time (days)"
                  type="number"
                  value={formData.operationalProfile.leadTimes.standard}
                  onChange={(e) => handleChange('operationalProfile.leadTimes.standard', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Rush Lead Time (days)"
                  type="number"
                  value={formData.operationalProfile.leadTimes.rush}
                  onChange={(e) => handleChange('operationalProfile.leadTimes.rush', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Defect Rate (%)"
                  type="number"
                  value={formData.operationalProfile.qualityMetrics.defectRate}
                  onChange={(e) =>
                    handleChange('operationalProfile.qualityMetrics.defectRate', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="On-Time Delivery (%)"
                  type="number"
                  value={formData.operationalProfile.qualityMetrics.onTimeDelivery}
                  onChange={(e) =>
                    handleChange('operationalProfile.qualityMetrics.onTimeDelivery', e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Supported Materials"
                  value={formData.operationalProfile.supportedMaterials}
                  onChange={(e) => handleChange('operationalProfile.supportedMaterials', e.target.value)}
                  placeholder="Comma separated list"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Garment Types"
                  value={formData.operationalProfile.garmentTypes}
                  onChange={(e) => handleChange('operationalProfile.garmentTypes', e.target.value)}
                  placeholder="Comma separated list"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Internal Notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            Save Changes
          </Button>
          <Button
            variant="outlined"
            onClick={loadPartner}
            disabled={saving}
          >
            Reset
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={saving}
          >
            Delete Partner
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PartnerDetailPage;
